import { IonAccordion, IonAccordionGroup, IonItem } from "@ionic/react";
import { ReactNode, useMemo, useState } from "react";

import styles from "@components/accordions/Accordion.module.css";
import { generateHTMLId } from "@utils/htmlUtils";

type Props = {
  border?: boolean;
  // We have to implement a border that is specific to the background color of the accordion (Ionic
  // does not add borders to accordions by default), and to do this we have to write a custom style
  // based on the color of the accordion. Because these styles are manual, we should limit the color
  // prop to ones for which we know we've handled the border case. If you add a color here, make
  // sure you handle the appropriate border by adding a style for it to Accordion.module.css.
  color?:
    | "light-blue"
    | "light-red"
    | "light-yellow"
    | "mint"
    | "primary"
    | "primary-50"
    | "primary-100"
    | "secondary"
    | "secondary-100"
    | "white";
  content: ReactNode;
  defaultOpen?: boolean;
  header: ReactNode;
};

const Accordion = ({ border = true, color = "white", content, defaultOpen = false, header }: Props) => {
  const wrapperStyles = [styles.accordion, styles[color]];
  if (border) {
    wrapperStyles.push(styles.border);
  }

  const accordionValue = useMemo(() => generateHTMLId(), []);

  const [accordionGroupValue, setAccordionGroupValue] = useState<string | null>(defaultOpen ? accordionValue : null);

  /* eslint-disable react/forbid-elements */
  return (
    <IonAccordionGroup
      class={wrapperStyles.join(" ")}
      color={color}
      onIonChange={e => {
        if ([null, undefined, accordionValue].includes(e.detail.value)) {
          setAccordionGroupValue(e.detail.value);
        }
      }}
      value={accordionGroupValue}
    >
      <IonAccordion color={color} value={accordionValue}>
        <IonItem color={color} lines="none" slot="header">
          {header}
        </IonItem>
        {content}
      </IonAccordion>
    </IonAccordionGroup>
  );
  /* eslint-enable react/forbid-elements */
};

export default Accordion;
